import type { CountryItem } from "@/types/country";
import {
  DataProviderItem,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDataColumn,
  DatasetDataType,
  DatasetDetailResponse,
  DatasetExploreItem,
  DatasetFilter,
  DatasetMetadata,
  DatasetSchemaLabel,
  EFilterOptionType,
  FilterOption,
  GetDatasetListResponse
} from "@/types/dataset";
import {
  AggregationType,
  OrderByType,
  PostWidgetDefaultConfigResponse,
  WidgetDefaultType,
  WidgetType
} from "@/types/widget";

export type ExploreDatasetMocks = typeof EXPLORE_DATASET_MOCKS;

/**
 * Country-specific *display* fields for the onboarding target dataset.
 *
 * Display-only by design: the dataset id, provider, source and schema stay
 * stable across countries so the tour's click-orchestration and the downstream
 * add / attribute-filter seams — which key off `ONBOARDING_DATASET_ID` and
 * `datasetDetail.id` — are country-invariant. Only the user-visible fields swap.
 */
type TargetDatasetDisplay = Pick<
  DatasetExploreItem,
  "name" | "country_id" | "bbox" | "short_description"
>;

/** Countries with a localized onboarding target dataset; others fall back to ID. */
export type OnboardingDatasetCountry = keyof typeof ONBOARDING_COUNTRY_FIXTURES;

/**
 * Deterministic mock data for the `explore-dataset` onboarding module.
 *
 * During onboarding the Dataset Explorer must show fixed data with zero network
 * — no live dataset list that changes, no loading spinners, no error toasts.
 * These fixtures are the single source of that data; the onboarding-aware
 * wrapper hooks (see spec §4.2/§4.3) return them when `onboarding.active`.
 *
 * Everything is fully typed against the real response types so the wrappers can
 * substitute these for the live hook results without casts. IDs are fixed so
 * the tour's click orchestration can target the same dataset every run.
 *
 * Production-safe: this lives under `onboarding/configs` (shipped in the app
 * bundle) and depends only on type definitions + pure literals — NOT on the
 * `src/utils/test` factories, which are test-only infrastructure.
 */

/** Fixed id for the dataset the tour selects and adds. */
export const ONBOARDING_DATASET_ID = "onboarding-poi-dataset";

/** Fixed id for the mock data provider shown in the source filter. */
export const ONBOARDING_PROVIDER_ID = "onboarding-provider";

/**
 * Base explore-list fields shared by the card grid, the detail response, and
 * the metadata. POI / subscribed so the add flow paints points on the map and
 * the attribute filter is enabled.
 */
const targetDatasetItem: DatasetExploreItem = {
  bbox: [95, -11, 141, 6],
  country_id: "id",
  created_at: "2025-03-06T12:11:20.410939Z",
  id: ONBOARDING_DATASET_ID,
  is_subscribed: true,
  license: "premium",
  name: "Shop and Retail - POI Latest (ID)",
  provider: {
    id: ONBOARDING_PROVIDER_ID,
    logo_asset_url: "https://dev.lokasi.com/asset/li-bvt-logo.svg",
    name: "Bvarta"
  },
  published: true,
  short_description:
    "Latest POI data for minimarkets, supermarkets, traditional markets, shopping malls, fashion stores, electronic stores, grocery stores, and household goods shops across Indonesia. Updated quarterly.",
  size_kb: 0,
  source: "bvt",
  spatial_aggregation_type: "poi",
  updated_at: "2026-06-24T19:49:37.744446Z",
  total_rows: 0
};

/** Filler cards so the grid looks populated. All selectable, none is the tour target. */
const fillerDatasetItems: DatasetExploreItem[] = [
  {
    bbox: [95, -11, 141, 6],
    country_id: "id",
    created_at: "2025-03-06T12:11:20.410939Z",
    id: "92944889-76f3-4fa9-83be-b20ff3757569",
    is_subscribed: true,
    license: "premium",
    name: "Food and Beverage - POI Latest (ID)",
    provider: {
      id: "4920bee2-38dd-48c3-ac5f-52ae00a27958",
      logo_asset_url: "https://dev.lokasi.com/asset/li-bvt-logo.svg",
      name: "Bvarta"
    },
    published: true,
    short_description:
      "Latest POI data for restaurants, cafes, coffee shops, fast food outlets, cake and bakery shops, street food stalls, food halls, and catering services across Indonesia. Updated quarterly.",
    size_kb: 0,
    source: "bvt",
    spatial_aggregation_type: "poi",
    updated_at: "2026-06-24T19:38:39.72692Z",
    total_rows: 0
  },
  {
    bbox: [95, -11, 141, 6],
    country_id: "id",
    created_at: "2025-03-06T12:11:20.410939Z",
    id: "a7f253d6-6fd9-4a7d-a9f1-ded71170746f",
    is_subscribed: true,
    license: "premium",
    name: "Demographics by Age - Admin Village Latest (ID)",
    provider: {
      id: "4920bee2-38dd-48c3-ac5f-52ae00a27958",
      logo_asset_url: "https://dev.lokasi.com/asset/li-bvt-logo.svg",
      name: "Bvarta"
    },
    published: true,
    short_description:
      "A comprehensive demographic data of the population distribution by age at the village level throughout Indonesia.",
    size_kb: 0,
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    updated_at: "2026-06-02T05:01:37.156773Z",
    total_rows: 0
  },
  {
    bbox: [95, -11, 141, 6],
    country_id: "id",
    created_at: "2025-03-06T12:11:20.410939Z",
    id: "0e6036b4-3f40-4953-93cc-c8c5298b2b5e",
    is_subscribed: true,
    license: "premium",
    name: "Demographics by Religion - Admin Village Latest (ID)",
    provider: {
      id: "4920bee2-38dd-48c3-ac5f-52ae00a27958",
      logo_asset_url: "https://dev.lokasi.com/asset/li-bvt-logo.svg",
      name: "Bvarta"
    },
    published: true,
    short_description:
      "A comprehensive demographic data of the population distribution by religion at the village level throughout Indonesia.",
    size_kb: 0,
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    updated_at: "2026-06-02T05:07:58.039732Z",
    total_rows: 0
  }
];

/** Dataset list response — the target card is first so the tour can click it. */
const datasetList: GetDatasetListResponse = {
  data: [targetDatasetItem, ...fillerDatasetItems],
  meta: {
    pagination: {
      next: 1,
      prev: 1,
      per_page: 10,
      total: 1 + fillerDatasetItems.length,
      total_pages: 1
    }
  }
};

/** Countries for the country filter. "All" / global is derived by the UI. */
const countries: CountryItem[] = [
  {
    id: "global",
    name: "Global",
    point: {
      latitude: 0,
      longitude: 0
    }
  },
  {
    id: "id",
    name: "Indonesia",
    point: {
      latitude: -6.20278,
      longitude: 106.84944
    }
  },
  {
    id: "jp",
    name: "Japan",
    point: {
      latitude: 35.69722,
      longitude: 139.70833
    }
  },
  {
    id: "vn",
    name: "Vietnam",
    point: {
      latitude: 21.03333,
      longitude: 105.85
    }
  }
];

/** Single mock provider for the data-source filter. */
const dataProviders: DataProviderItem[] = [
  {
    id: "4920bee2-38dd-48c3-ac5f-52ae00a27958",
    logo_asset_url: "https://dev.lokasi.com/asset/li-bvt-logo.svg",
    name: "Bvarta"
  },
  {
    id: "0397a78b-c8ca-4f1c-a358-0e9b50a74e77",
    logo_asset_url: "https://dev.lokasi.com/asset/here-logo.png",
    name: "here"
  },
  {
    id: "8231c256-30a0-44e4-b7f8-4da1325f11a3",
    logo_asset_url: "https://dev.lokasi.com/asset/mapbox-logo.png",
    name: "Mapbox"
  },
  {
    id: "2b9b98e7-2931-4671-acfe-e7f5d134e692",
    logo_asset_url: "https://dev.lokasi.com/asset/rw-logo.png",
    name: "Ray White Indonesia"
  },
  {
    id: "3169fec1-ee0c-4120-b74a-aa325c9e241a",
    logo_asset_url: "https://dev.lokasi.com/asset/sewa-reklame.png",
    name: "Sewa Reklame"
  },
  {
    id: "9fe105ed-1ae6-4d02-b131-55a5a990a7db",
    logo_asset_url: "https://dev.lokasi.com/asset/visa-logo.png",
    name: "Visa"
  },
  {
    id: "262f9b26-7d62-48c1-aaf2-2cb9bcf964f0",
    logo_asset_url: "https://preprod.lokasi.com/asset/estageo-logo.png",
    name: "XLSmart"
  }
];

// --- Attribute schema -------------------------------------------------------
// One reference-id column, one reference-geo (WKT point) column, and two plain
// text attributes the user can filter on.

// Real `Shop and Retail - POI` attribute schema, copied verbatim from a BE
// response. It is asserted through `unknown` to `DatasetAttributeSchema[]`:
// the live JSON is a superset of the strict FE type (string `data_type`, `null`
// data labels, extra dimension/measure/breakdown flags), exactly as the network
// layer delivers it. The runtime values are what the converter + the filter
// derivation (`generateAttributeFilterOptions`) actually read.
const schema = [
  {
    attribute_code: "",
    attribute_column: "poi_id",
    attribute_name: "POI ID",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "int",
    group_by_attr: false,
    id: "c3118f9e-2c0e-41ff-a040-bb5df2462de7",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: true,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 1,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "",
    attribute_column: "name",
    attribute_name: "Name",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: "title",
    data_type: "string",
    group_by_attr: false,
    id: "dcce03ee-3976-4f34-8d1a-595355ae9da2",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 2,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "",
    attribute_column: "address",
    attribute_name: "Address",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: "address",
    data_type: "string",
    group_by_attr: false,
    id: "bddb8c7d-cabb-4498-8204-0e9327eb8ba9",
    is_dimension: false,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 3,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "",
    attribute_column: "location",
    attribute_name: "Location",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: "point",
    data_type: "geometry",
    group_by_attr: false,
    id: "c530a2d3-54e7-4da2-b39d-24bbd7caab44",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: true,
    is_reference_id: false,
    is_spatial: true,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 4,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "",
    attribute_column: "phone",
    attribute_name: "Phone",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "a94c7520-6d35-4d6f-9ce5-4744072e244f",
    is_dimension: false,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 5,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "",
    attribute_column: "market_target",
    attribute_name: "Market Target",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "4580bad7-eec2-4b58-a531-9eccf5b4d3d8",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 6,
    time_format: null,
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "mitra",
    attribute_name: "Mitra",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "f4ec5c22-c4d5-4a5c-92bc-902b032342da",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 7,
    time_format: null,
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "ownership",
    attribute_name: "Ownership",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "5d258745-be45-4c32-9344-1e12f111a379",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 8,
    time_format: null,
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "product",
    attribute_name: "Product",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "58e4807d-9ffd-474e-8815-30d886fff9b8",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 9,
    time_format: null,
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "type",
    attribute_name: "Type",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3a8eb8eb-8291-4fac-952a-5c38824df0c0",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 10,
    time_format: null,
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "service",
    attribute_name: "Service",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "dd09d106-f16e-4fe8-b671-1104863d4317",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 11,
    time_format: null,
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "brand_code",
    attribute_name: "Brand Code",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "b778aacd-4380-4d16-9a93-280c4a74509a",
    is_dimension: false,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: false,
    paired_key_attribute_id: null,
    position: 12,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "",
    attribute_column: "category_code",
    attribute_name: "Category Code",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-07-05T10:50:32.883339Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3965d083-0315-424b-a74e-0d17d35f97d7",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: false,
    paired_key_attribute_id: null,
    position: 13,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "",
    attribute_column: "brand_name",
    attribute_name: "Brand",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-08-25T09:32:00.845467Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "a2b1246e-0dc7-4e93-b211-841dbb541d87",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: "b778aacd-4380-4d16-9a93-280c4a74509a",
    position: 14,
    time_format: "",
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "category_name",
    attribute_name: "Category",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-08-25T09:32:00.845467Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "d9b912d5-1cd2-475e-ba41-8ab8d6fd7875",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: "3965d083-0315-424b-a74e-0d17d35f97d7",
    position: 15,
    time_format: "",
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "group_name",
    attribute_name: "Group",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-08-25T09:32:00.845467Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "13bc03d1-36fc-469d-ae4d-f99608ce5955",
    is_dimension: true,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: "efd06fa4-13d6-4a91-a601-c3a34f37fdaa",
    position: 16,
    time_format: "",
    updated_at: "2026-05-20T08:08:09.603805Z"
  },
  {
    attribute_code: "",
    attribute_column: "group_code",
    attribute_name: "Group Code",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "2025-11-20T01:42:24.760604Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "efd06fa4-13d6-4a91-a601-c3a34f37fdaa",
    is_dimension: false,
    is_measure: false,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: false,
    paired_key_attribute_id: null,
    position: 17,
    time_format: null,
    updated_at: "2026-05-28T04:08:49.008148Z"
  },
  {
    attribute_code: "adm_area_level_4",
    attribute_column: "village_name",
    attribute_name: "Village Name",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: true,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 18,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  },
  {
    attribute_code: "adm_area_level_4",
    attribute_column: "village_code",
    attribute_name: "Village Code",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 19,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  },
  {
    attribute_code: "adm_area_level_3",
    attribute_column: "district_name",
    attribute_name: "District Name",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: true,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 20,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  },
  {
    attribute_code: "adm_area_level_3",
    attribute_column: "district_code",
    attribute_name: "District Code",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 21,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  },
  {
    attribute_code: "adm_area_level_2",
    attribute_column: "city_name",
    attribute_name: "City Name",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: true,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 22,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  },
  {
    attribute_code: "adm_area_level_2",
    attribute_column: "city_code",
    attribute_name: "City Code",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 23,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  },
  {
    attribute_code: "adm_area_level_1",
    attribute_column: "province_name",
    attribute_name: "Province Name",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: true,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 24,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  },
  {
    attribute_code: "adm_area_level_1",
    attribute_column: "province_code",
    attribute_name: "Province Code",
    breakdown_group_id: null,
    breakdown_group_label: null,
    created_at: "0001-01-01T00:00:00Z",
    data_label: null,
    data_type: "string",
    group_by_attr: false,
    id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    is_dimension: false,
    is_measure: false,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    is_visible: true,
    paired_key_attribute_id: null,
    position: 25,
    time_format: null,
    updated_at: "0001-01-01T00:00:00Z"
  }
] as unknown as DatasetAttributeSchema[];

/** Detail response passed to `handleAddDataset` as `datasetMetadata`. */
const datasetDetail: DatasetDetailResponse = {
  ...targetDatasetItem,
  schema: schema as DatasetDetailResponse["schema"]
};

/** Metadata (explore item + attribute schema) for the attribute filter. */
const datasetMetadata: DatasetMetadata = {
  ...targetDatasetItem,
  schema
};

/**
 * Filters applied to the target dataset by default during onboarding, so the
 * attribute-filter panel opens in a realistic pre-filtered state (a Province
 * admin-area filter + a Category multi-select). The attribute-filter seam
 * (task 3) seeds `useAttributeFilter` with these as the initial `savedFilters`.
 *
 * `dataset_id` is set to the onboarding dataset id so the filters belong to the
 * target dataset; the `filterId` suffixes (`_24`, `_15`) match the source
 * attribute `position` (Province Name, Category) in `schema` above.
 */
const savedFilters: DatasetFilter[] = [
  {
    filterId: "3c74c799-6cdb-4c76-9cef-1129a655f0bd_24",
    filterLabel: "Province Name",
    attributeCode: "adm_area_level_1",
    attributeId: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    filterType: EFilterOptionType.MULTIPLE,
    paired_key_attribute_id: null,
    selectedOptions: ["70294ab3-b8f0-4e17-8115-c0a5241b4edf::31"],
    is_multiple_adm_area: true
  },
  {
    filterId: "d9b912d5-1cd2-475e-ba41-8ab8d6fd7875_15",
    filterLabel: "Category",
    attributeCode: "",
    attributeId: "d9b912d5-1cd2-475e-ba41-8ab8d6fd7875",
    filterType: EFilterOptionType.MULTIPLE,
    time_format: "",
    paired_key_attribute_id: "3965d083-0315-424b-a74e-0d17d35f97d7",
    dataset_id: ONBOARDING_DATASET_ID,
    data_type: "string",
    selectedOptions: ["210704", "211405", "211804", "210703", "211806"]
  }
];

// NOTE: there is intentionally no hand-authored `filterOptions` fixture. The
// attribute-filter panel derives its options from the dataset schema at runtime
// via `generateAttributeFilterOptions(datasetMetadata)` (see useAttributeFilter
// → datasetAttributeHelper). Mocking `datasetMetadata.schema` above is enough —
// the real derivation runs and produces the options, so there is one source of
// truth and no risk of the two drifting.

// --- Raw dataset data (painted on the map) ----------------------------------
// Column order matches each row's value order, and `name` mirrors the schema's
// `attribute_name` so the feature-collection converter matches columns to the
// schema. The `Location` column is the reference-geo WKT POINT the map renders.
// `schema_id` references the real attribute ids from `schema` above.

const columns: DatasetDataColumn[] = [
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "poi_id",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "POI ID",
    schema_id: "c3118f9e-2c0e-41ff-a040-bb5df2462de7",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "name",
    data_label: DatasetSchemaLabel.TITLE,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Name",
    schema_id: "dcce03ee-3976-4f34-8d1a-595355ae9da2",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "address",
    data_label: DatasetSchemaLabel.ADDRESS,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Address",
    schema_id: "bddb8c7d-cabb-4498-8204-0e9327eb8ba9",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "location",
    data_label: DatasetSchemaLabel.POINT,
    data_type: DatasetDataType.TYPE_GEOMETRY,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: true,
    is_visible: true,
    name: "Location",
    schema_id: "c530a2d3-54e7-4da2-b39d-24bbd7caab44",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "phone",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Phone",
    schema_id: "a94c7520-6d35-4d6f-9ce5-4744072e244f",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "market_target",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Market Target",
    schema_id: "4580bad7-eec2-4b58-a531-9eccf5b4d3d8",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "mitra",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Mitra",
    schema_id: "f4ec5c22-c4d5-4a5c-92bc-902b032342da",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "ownership",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Ownership",
    schema_id: "5d258745-be45-4c32-9344-1e12f111a379",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "product",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Product",
    schema_id: "58e4807d-9ffd-474e-8815-30d886fff9b8",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "type",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Type",
    schema_id: "3a8eb8eb-8291-4fac-952a-5c38824df0c0",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "service",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Service",
    schema_id: "dd09d106-f16e-4fe8-b671-1104863d4317",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "brand_code",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: false,
    name: "Brand Code",
    schema_id: "b778aacd-4380-4d16-9a93-280c4a74509a",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "category_code",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: false,
    name: "Category Code",
    schema_id: "3965d083-0315-424b-a74e-0d17d35f97d7",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "brand_name",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Brand",
    schema_id: "a2b1246e-0dc7-4e93-b211-841dbb541d87",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "category_name",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Category",
    schema_id: "d9b912d5-1cd2-475e-ba41-8ab8d6fd7875",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "group_name",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Group",
    schema_id: "13bc03d1-36fc-469d-ae4d-f99608ce5955",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "group_code",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: false,
    name: "Group Code",
    schema_id: "efd06fa4-13d6-4a91-a601-c3a34f37fdaa",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "village_name",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Village Name",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "village_code",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Village Code",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "district_name",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "District Name",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "district_code",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "District Code",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "city_name",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "City Name",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "city_code",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "City Code",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "province_name",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: true,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Province Name",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  },
  {
    breakdown_group_id: null,
    breakdown_group_label: null,
    code: "province_code",
    data_label: null,
    data_type: DatasetDataType.TYPE_STRING,
    is_dimension: false,
    is_measure: false,
    is_reference_column: false,
    is_reference_geo: false,
    is_visible: true,
    name: "Province Code",
    schema_id: "3c74c799-6cdb-4c76-9cef-1129a655f0bd",
    time_format: null
  }
];

// Six shop/retail POIs around central Jakarta. Column order:
// [POI ID, Name, Address, Location (WKT `POINT (lng lat)`), Category].
const idRows: DatasetData["rows"] = [
  [
    8365076,
    "Indomaret Boulevard JGC (TWNC)",
    "Jl. JKT Garden City Boulevard No. 52",
    "POINT(106.952568 -6.17988)",
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    "210703016",
    "210703",
    "Indomaret",
    "Minimarket",
    "Modern Store",
    "2107",
    "CAKUNG TIMUR",
    "3175061004",
    "CAKUNG",
    "317506",
    "JAKARTA TIMUR",
    "3175",
    "DKI JAKARTA",
    "31"
  ],
  [
    8365058,
    "Indomaret ",
    "Petukangan Utara",
    "POINT(106.747564 -6.236268)",
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    "210703016",
    "210703",
    "Indomaret",
    "Minimarket",
    "Modern Store",
    "2107",
    "PETUKANGAN UTARA",
    "3174101003",
    "PESANGGRAHAN",
    "317410",
    "JAKARTA SELATAN",
    "3174",
    "DKI JAKARTA",
    "31"
  ],
  [
    8363727,
    "Alfamart Kyai Maja",
    "10, Jl. Kyai Maja No.37, RT.10/RW.7",
    "POINT(106.793194 -6.241536)",
    "+62217227596",
    null,
    null,
    null,
    null,
    null,
    null,
    "210703003",
    "210703",
    "Alfamart",
    "Minimarket",
    "Modern Store",
    "2107",
    "KRAMAT PELA",
    "3174071003",
    "KEBAYORAN BARU",
    "317407",
    "JAKARTA SELATAN",
    "3174",
    "DKI JAKARTA",
    "31"
  ],
  [
    12457895,
    "PASAR GG 8",
    "Gg. XI No.47 8, RT.11/RW.6",
    "POINT(106.877754 -6.121458)",
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    "211806400",
    "211806",
    "Unspecified",
    "Traditional Market",
    "Shoping Center",
    "2118",
    "WARAKAS",
    "3172021007",
    "TANJUNG PRIOK",
    "317202",
    "JAKARTA UTARA",
    "3172",
    "DKI JAKARTA",
    "31"
  ],
  [
    12428936,
    "Alfamart bintaro sektor 1-2",
    "Jl. Taman Bintaro Barat No.14, RT.14/RW.8",
    "POINT(106.75485 -6.2698)",
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    "210703003",
    "210703",
    "Alfamart",
    "Minimarket",
    "Modern Store",
    "2107",
    "BINTARO",
    "3174101002",
    "PESANGGRAHAN",
    "317410",
    "JAKARTA SELATAN",
    "3174",
    "DKI JAKARTA",
    "31"
  ],
  [
    12886019,
    "Toko Eka Jaya",
    "Jl. H. Mugeni 1 No.13 13, RT.13/RW.4",
    "POINT(106.87818 -6.209611)",
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    "210703400",
    "210703",
    "Unspecified",
    "Minimarket",
    "Modern Store",
    "2107",
    "PISANGAN TIMUR",
    "3175021002",
    "PULOGADUNG",
    "317502",
    "JAKARTA TIMUR",
    "3175",
    "DKI JAKARTA",
    "31"
  ],
  [
    13219865,
    "Hari Hari Pasar Swalayan Buaran Plaza",
    "Jl. Raden Intan Raya No.14",
    "POINT(106.924299 -6.217608)",
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    "210704040",
    "210704",
    "Hari Hari Supermarket",
    "Supermarket",
    "Modern Store",
    "2107",
    "KLENDER",
    "3175071003",
    "DUREN SAWIT",
    "317507",
    "JAKARTA TIMUR",
    "3175",
    "DKI JAKARTA",
    "31"
  ],
  [
    7878335,
    "Alfamart Stasiun Kalideres",
    "Semanan",
    "POINT(106.703745 -6.166065)",
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    "210703003",
    "210703",
    "Alfamart",
    "Minimarket",
    "Modern Store",
    "2107",
    "SEMANAN",
    "3173061002",
    "KALIDERES",
    "317306",
    "JAKARTA BARAT",
    "3173",
    "DKI JAKARTA",
    "31"
  ],
  [
    9598073,
    "Sinar Agung",
    "Pasar Minggu",
    "POINT(106.843218 -6.285361)",
    "+6281284616864",
    null,
    null,
    null,
    null,
    null,
    null,
    "211405400",
    "211405",
    "Unspecified",
    "Electronic Store",
    "Electronic Shop",
    "2114",
    "PASAR MINGGU",
    "3174041001",
    "PASAR MINGGU",
    "317404",
    "JAKARTA SELATAN",
    "3174",
    "DKI JAKARTA",
    "31"
  ]
];

const idDatasetData: DatasetData = {
  columns,
  rows: idRows,
  dataset_id: ONBOARDING_DATASET_ID,
  dataset_size_kb: 12,
  name: targetDatasetItem.name,
  display_name: targetDatasetItem.display_name,
  spatial_aggregation_type: "poi"
};

/** Input-rows count for the `/stats` check — well under any limit. */
const statsCount = idRows.length;

/**
 * Filter candidate values — empty during onboarding. The tour never opens a
 * filter to pick values, and an empty list keeps the attribute-filter panel
 * deterministic (no candidate-value network call). Stable reference so the
 * wrapper can substitute it as a success result.
 */
const attributeValues: FilterOption[] = [];

/**
 * Default widget config for the onboarding dataset. Defense-in-depth: the add
 * flow does NOT attach a widget during onboarding (spec §4.3 keeps the painted
 * dataset widget-free to avoid downstream widget-analysis network), but the
 * widget-config wrapper resolves this fixture if the mutation is ever invoked,
 * so that path stays zero-network and deterministic.
 */
const widgetDefaultConfig: PostWidgetDefaultConfigResponse = {
  data: [
    {
      is_default: WidgetDefaultType.SYSTEM_GENERATED,
      dataset_id: ONBOARDING_DATASET_ID,
      label: "",
      chart_type: WidgetType.DONUT,
      dimension: "category",
      measure: "id",
      aggregation: AggregationType.COUNT,
      configuration: { dimension_values_shown: 5, order_by: OrderByType.DESC }
    }
  ]
};

/**
 * The single fixtures bag consumed by the onboarding-aware wrapper hooks.
 *
 * Note: a couple of values are intentionally NOT prebuilt here.
 * - The Dataset slice entry: the add flow constructs it via `handleAddDataset`
 *   → `createNewDataset` from `datasetDetail` + `datasetMetadata.schema` +
 *   `datasetData`, exactly like the real SSE path.
 * - `filterOptions`: derived at runtime from `datasetMetadata.schema` via
 *   `generateAttributeFilterOptions`, so the schema is the single source.
 */
export const EXPLORE_DATASET_MOCKS = {
  datasetList,
  countries,
  dataProviders,
  datasetDetail,
  datasetMetadata,
  savedFilters,
  statsCount,
  datasetData: idDatasetData,
  attributeValues,
  widgetDefaultConfig
} as const;

/** Vietnam filler cards so the VN grid looks populated with VN datasets. */
const vnFillerDatasetItems: DatasetExploreItem[] = [
  {
    bbox: [95, -11, 141, 6],
    country_id: "vn",
    created_at: "2025-07-13T11:59:51.417118Z",
    id: "66266cbb-c1d5-4a64-bb3f-bbd44e5f3e03",
    is_subscribed: true,
    license: "premium",
    name: "Food and Beverage - POI Latest (VN)",
    provider: {
      id: "4920bee2-38dd-48c3-ac5f-52ae00a27958",
      logo_asset_url: "https://dev.lokasi.com/asset/li-bvt-logo.svg",
      name: "Bvarta"
    },
    published: true,
    short_description:
      "Latest POI data for restaurants, cafes, coffee shops, fast food outlets, cake and bakery shops, street food stalls, food halls, and catering services across Vietnam. Updated quarterly.",
    size_kb: 0,
    source: "bvt",
    spatial_aggregation_type: "poi",
    updated_at: "2026-06-26T19:45:45.26693Z",
    total_rows: 0
  },
  {
    country_id: "vn",
    created_at: "2026-05-21T05:27:32.023499Z",
    id: "50ea4f18-ff3b-4a88-92ec-6eeceacf2ffa",
    is_subscribed: true,
    license: "premium",
    name: "Demographic Age Group by Gender - Admin Province 2024 (VN)",
    provider: {
      id: "4920bee2-38dd-48c3-ac5f-52ae00a27958",
      logo_asset_url: "https://dev.lokasi.com/asset/li-bvt-logo.svg",
      name: "Bvarta"
    },
    published: true,
    short_description:
      "A comprehensive demographic dataset capturing population distribution by specific age groups and gender at the province level across Vietnam for the year 2024, focusing on the youth (0-14) and elderly (60 and over) segments.",
    size_kb: 0,
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    updated_at: "2026-05-21T05:27:32.023499Z",
    total_rows: 0
  }
];

/**
 * Vietnam default saved filters (a VN Province admin-area filter + a Category
 * multi-select), so the VN attribute-filter panel opens pre-filtered like the
 * ID one. `dataset_id` is pinned to `ONBOARDING_DATASET_ID` — the onboarding
 * target keeps its synthetic id across countries (display-only), so the filters
 * still belong to the painted target dataset.
 */
const vnSavedFilters: DatasetFilter[] = [
  {
    filterId: "c625fa0a-7769-473f-9f2d-580c4a11d732_22",
    filterLabel: "Province Name",
    attributeCode: "adm_area_level_1",
    attributeId: "c625fa0a-7769-473f-9f2d-580c4a11d732",
    filterType: EFilterOptionType.MULTIPLE,
    paired_key_attribute_id: null,
    selectedOptions: ["5f46b90e-e0b9-4d13-b27d-e3977f873292::VN024"],
    is_multiple_adm_area: true
  },
  {
    filterId: "3ca1ff5d-8fdb-4b4e-81b6-bc0a31e5ba78_11",
    filterLabel: "Category",
    attributeCode: "",
    attributeId: "3ca1ff5d-8fdb-4b4e-81b6-bc0a31e5ba78",
    filterType: EFilterOptionType.MULTIPLE,
    time_format: "",
    paired_key_attribute_id: "f9a33706-d2ab-43c7-929d-8932fa908ee6",
    dataset_id: ONBOARDING_DATASET_ID,
    data_type: "string",
    selectedOptions: ["210704", "211405", "211804", "210703", "211806"]
  }
];

// Six shop/retail POIs around central Ha Noi. Column order:
// [POI ID, Name, Address, Location (WKT `POINT (lng lat)`), Category].
const vnRows: DatasetData["rows"] = [
  [
    28812844,
    "211405",
    "Cửa hàng điện tử Huân Hoa",
    "POINT(105.867651 21.137472)",
    "Cao Lỗ, Việt Hùng, Đông Anh, Hà Nội",
    null,
    "211405400",
    "Unspecified",
    null,
    null,
    "Electronic Store",
    null,
    "Electronic Shop",
    null,
    null,
    null,
    "2114",
    "Viet Hung",
    "VN0240070020",
    "Dong Anh",
    "VN024007",
    "Ha Noi",
    "VN024"
  ],
  [
    25261053,
    "211804",
    "Cty TNHH Hangori",
    "POINT(105.930801 21.093643)",
    "Đ. Ninh Hiệp, thôn Trùng Quán, Gia Lâm, Hà Nội, Vietnam",
    null,
    "211804400",
    "Unspecified",
    null,
    null,
    "Shopping Mall",
    null,
    "Shoping Center",
    null,
    null,
    null,
    "2118",
    "Yen Thuong",
    "VN0240090020",
    "Gia Lam",
    "VN024009",
    "Ha Noi",
    "VN024"
  ],
  [
    28230373,
    "210703",
    "Tạp Hoá Sáu Lợi",
    "POINT(105.555628 21.010757)",
    "Nạ Bằng, Thạch Thất, Hanoi",
    null,
    "210703400",
    "Unspecified",
    null,
    null,
    "Minimarket",
    null,
    "Modern Store",
    null,
    null,
    null,
    "2107",
    "Ha Bang",
    "VN0240250010",
    "Thach That",
    "VN024025",
    "Ha Noi",
    "VN024"
  ],
  [
    27367460,
    "210703",
    "Cửa Hàng Tạp Hóa Chú Giảng",
    "POINT(105.632657 20.927961)",
    "Đông Cựu, Chương Mỹ, Hanoi",
    null,
    "210703400",
    "Unspecified",
    null,
    null,
    "Minimarket",
    null,
    "Modern Store",
    null,
    null,
    null,
    "2107",
    "Dong Yen",
    "VN0240050005",
    "Chuong My",
    "VN024005",
    "Ha Noi",
    "VN024"
  ],
  [
    28362437,
    "211405",
    "tủ lạnh nhật nội địa _máy giặt nhật bãi _điêu hòa nhật ,Hàng Nhật Nội Địa",
    "POINT(105.816958 21.016776)",
    "68c Ng. 87 P. Láng Hạ, Chợ Dừa, Đống Đa, Hà Nội 100000, Vietnam",
    null,
    "211405400",
    "Unspecified",
    null,
    null,
    "Electronic Store",
    null,
    "Electronic Shop",
    null,
    null,
    null,
    "2114",
    "Trung Liet",
    "VN0240080017",
    "Dong Da",
    "VN024008",
    "Ha Noi",
    "VN024"
  ],
  [
    29410982,
    "211806",
    "THỊT THỎ HÀ NỘI",
    "POINT(105.76656 20.943475)",
    "Nhân Trạch, Hà Đông, Hanoi",
    null,
    "211806400",
    "Unspecified",
    null,
    null,
    "Traditional Market",
    null,
    "Shoping Center",
    null,
    null,
    null,
    "2118",
    "Phu Luong",
    "VN0240100011",
    "Ha Dong",
    "VN024010",
    "Ha Noi",
    "VN024"
  ],
  [
    26971560,
    "211804",
    "Đại lí Apple8 Store",
    "POINT(105.851684 21.025305)",
    "13 P. Bà Triệu, Tràng Tiền, Hoàn Kiếm, Hà Nội",
    null,
    "211804400",
    "Unspecified",
    null,
    null,
    "Shopping Mall",
    null,
    "Shoping Center",
    null,
    null,
    null,
    "2118",
    "Trang Tien",
    "VN0240130018",
    "Hoan Kiem",
    "VN024013",
    "Ha Noi",
    "VN024"
  ]
];

/**
 * VN raw dataset data painted on the map. Same column/schema layout as ID
 * (country-invariant), rows intentionally empty — to be filled in manually.
 * `name`/`display_name` are overridden by the factory with the VN target name.
 */
const vnDatasetData: DatasetData = {
  columns,
  rows: vnRows,
  dataset_id: ONBOARDING_DATASET_ID,
  dataset_size_kb: 0,
  name: "Shop and Retail - POI Latest (VN)",
  spatial_aggregation_type: "poi"
};

/**
 * All per-country onboarding overrides in one place, keyed by country code.
 *
 * Each entry carries everything that differs between countries: the target
 * dataset's display fields, the filler cards, the default saved filters, and the
 * painted dataset data. Adding a country = adding one entry here; the factory
 * reads a single lookup. Everything else (ids, provider, schema) is
 * country-invariant by design (display-only), so downstream seams keyed off
 * `ONBOARDING_DATASET_ID` keep working.
 */
type OnboardingCountryFixture = {
  display: TargetDatasetDisplay;
  fillerItems: DatasetExploreItem[];
  savedFilters: DatasetFilter[];
  datasetData: DatasetData;
};

const ONBOARDING_COUNTRY_FIXTURES = {
  id: {
    display: {
      name: targetDatasetItem.name,
      country_id: targetDatasetItem.country_id,
      bbox: targetDatasetItem.bbox,
      short_description: targetDatasetItem.short_description
    },
    fillerItems: fillerDatasetItems,
    savedFilters,
    datasetData: idDatasetData
  },
  vn: {
    display: {
      name: "Shop and Retail - POI Latest (VN)",
      country_id: "vn",
      bbox: [95, -11, 141, 6],
      short_description:
        "Latest POI data for minimarkets, supermarkets, traditional markets, shopping malls, fashion stores, electronic stores, grocery stores, and household goods shops across Vietnam. Updated quarterly."
    },
    fillerItems: vnFillerDatasetItems,
    savedFilters: vnSavedFilters,
    datasetData: vnDatasetData
  }
} satisfies Record<string, OnboardingCountryFixture>;

/**
 * Country-aware explore-dataset fixtures.
 *
 * Indonesia is the default: only Vietnam has localized mock data, so EVERY other
 * country (Japan, Malaysia, Global, unknown, or unset) returns the shared
 * `EXPLORE_DATASET_MOCKS` reference unchanged — existing referential-equality
 * call sites and tests stay untouched. Vietnam returns a fresh bag built from
 * its entry in `ONBOARDING_COUNTRY_FIXTURES`; only the user-visible fields swap,
 * the ids / provider / schema stay stable so every downstream seam keyed off
 * `ONBOARDING_DATASET_ID` keeps working.
 */
export function getExploreDatasetMocks(country?: string): ExploreDatasetMocks {
  // Default to the Indonesia bag for anything that isn't Vietnam — no other
  // country has onboarding mock data yet.
  if (country !== "vn") return EXPLORE_DATASET_MOCKS;

  const {
    display,
    fillerItems,
    savedFilters: countrySavedFilters,
    datasetData: countryDatasetData
  } = ONBOARDING_COUNTRY_FIXTURES.vn;
  const target: DatasetExploreItem = { ...targetDatasetItem, ...display };
  const data = [target, ...fillerItems];

  return {
    ...EXPLORE_DATASET_MOCKS,
    datasetList: {
      ...datasetList,
      data,
      meta: {
        ...datasetList.meta,
        pagination: {
          ...datasetList.meta.pagination,
          total: data.length,
          total_pages: 1
        }
      }
    },
    datasetDetail: { ...datasetDetail, ...display },
    datasetMetadata: { ...datasetMetadata, ...display },
    datasetData: {
      ...countryDatasetData,
      name: target.name,
      display_name: target.display_name
    },
    savedFilters: countrySavedFilters
  };
}
